import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BadgeIcon from "@mui/icons-material/Badge";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import CircleIcon from "@mui/icons-material/Circle";
import CloseIcon from "@mui/icons-material/Close";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import EmailIcon from "@mui/icons-material/Email";
import EmojiEmotionsRoundedIcon from "@mui/icons-material/EmojiEmotionsRounded";
import GroupsIcon from "@mui/icons-material/Groups";
import HomeIcon from "@mui/icons-material/Home";
import InboxIcon from "@mui/icons-material/Inbox";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MicIcon from "@mui/icons-material/Mic";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonIcon from "@mui/icons-material/Person";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PhoneIcon from "@mui/icons-material/Phone";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SchoolIcon from "@mui/icons-material/School";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SettingsIcon from "@mui/icons-material/Settings";
import StopIcon from "@mui/icons-material/Stop";
import VideoCallRoundedIcon from "@mui/icons-material/VideoCallRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import WorkIcon from "@mui/icons-material/Work";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogTitle,
  Divider,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Popover,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useChat } from "../contexts/ChatProvider";
import api from "../infra/api";
import AudioPlayer from "./AudioPlayer";
import ModalForwardAttendance from "./ModalForwardAttendance";
import ModalNewChat from "./ModalNewChat";

// Styled Components - Teams inspired design
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    margin: "16px",
    maxHeight: "calc(100vh - 32px)",
    maxWidth: "calc(100vw - 32px)",
    animation: "slideIn 0.3s ease-out",
  },
  "@keyframes slideIn": {
    "0%": {
      opacity: 0,
      transform: "scale(0.95) translateY(-10px)",
    },
    "100%": {
      opacity: 1,
      transform: "scale(1) translateY(0)",
    },
  },
}));

const HeaderBar = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 50%, #4a148c 100%)",
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(90deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)",
    pointerEvents: "none",
  },
}));

const TitleSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

const ChatIconWrapper = styled(Box)({
  width: "36px",
  height: "36px",
  borderRadius: "8px",
  background: "rgba(255, 255, 255, 0.2)",
  backdropFilter: "blur(10px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.3)",
    transform: "scale(1.05)",
  },
});

const HeaderTitle = styled(Typography)({
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "16px",
  letterSpacing: "0.3px",
  textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
});

const HeaderSubtitle = styled(Typography)({
  color: "rgba(255, 255, 255, 0.8)",
  fontSize: "12px",
  fontWeight: 400,
});

const ControlsSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const AddButton = styled(IconButton)(({ theme }) => ({
  color: "#ffffff",
  padding: "8px 16px",
  borderRadius: "6px",
  background: "rgba(255, 255, 255, 0.2)",
  transition: "all 0.2s ease",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.3)",
    transform: "scale(1.02)",
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: "rgba(255, 255, 255, 0.9)",
  padding: "8px",
  borderRadius: "6px",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "rgba(196, 49, 75, 0.9)",
    color: "#ffffff",
  },
}));

const StyledDialogContent = styled(DialogContent)({
  padding: 0,
  margin: 0,
  background: "#f5f5f5",
  display: "flex",
  height: "calc(100vh - 80px)",
});

// Chat specific styled components
const Sidebar = styled(Box)(({ theme }) => ({
  width: 320,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: "#fff",
}));

const SidebarHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const ConversationsTabsWrapper = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  gap: 0,
  backgroundColor: theme.palette.grey[100],
  minHeight: 48,
  "& .MuiTabs-root": { minHeight: 48, backgroundColor: "#efefef !important" },
  "& .MuiTabs-flexContainer": { gap: 6, padding: 4 },
  "& .MuiTab-root": {
    minHeight: 40,
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    transition: "all 0.2s ease",
    "&:hover": {
      color: theme.palette.text.primary,
      backgroundColor: "rgba(0,0,0,0.04)",
    },
  },
  "& .MuiTab-root.Mui-selected": {
    color: "#fff",
    backgroundColor: theme.palette.primary.main,
    boxShadow: "0 2px 8px rgba(123, 31, 162, 0.35)",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  "& .MuiTab-root.Mui-selected .MuiChip-root": {
    backgroundColor: "rgba(255,255,255,0.25)",
    color: "#fff",
  },
  "& .MuiTab-root.Mui-selected .tab-icon-external": {
    color: "#fff !important",
  },
  "& .MuiTab-root.Mui-selected .tab-icon-internal": {
    color: "#fff !important",
  },
  "& .MuiTabs-indicator": {
    display: "none",
  },
}));

const ExternalSubTabsWrapper = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  minHeight: 40,
  "& .MuiTabs-root": { minHeight: 40, backgroundColor: "transparent" },
  "& .MuiTabs-flexContainer": { gap: 4 },
  "& .MuiTab-root": {
    minHeight: 36,
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    transition: "all 0.2s ease",
    minWidth: "auto",
    px: 1.5,
  },
  "& .MuiTab-root.Mui-selected": {
    color: theme.palette.text.primary,
    backgroundColor: "transparent",
  },
  "& .MuiTabs-indicator": {
    display: "none",
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const SearchInput = styled(TextField)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1),
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  "&:hover": {
    backgroundColor: theme.palette.grey[200],
  },
}));

const ChatList = styled(List)(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: 0,
}));

const ChatListItem = styled(ListItem)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRadius: 0,
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.grey[50],
  },
  "&.active": {
    backgroundColor: theme.palette.primary.light + "20",
    borderRight: `3px solid ${theme.palette.primary.main}`,
  },
}));

const ChatArea = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "white",
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: "hidden",
  position: "relative",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#fafafa",
}));

const ChatBackground = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: `url(${process.env.PUBLIC_URL || ""}/img/chat-bg.png)`,
  backgroundRepeat: "repeat",
  opacity: 0.4,
  pointerEvents: "none",
}));

const MessagesScroll = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: theme.spacing(1),
  position: "relative",
  zIndex: 1,
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isOwn",
})<{ isOwn: boolean }>(({ theme, isOwn }) => ({
  maxWidth: "70%",
  margin: theme.spacing(0.5, 0),
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(2),
  backgroundColor: isOwn ? "#7b1fa2" : "white",
  color: isOwn ? "white" : theme.palette.text.primary,
  alignSelf: isOwn ? "flex-end" : "flex-start",
  boxShadow: theme.shadows[1],
}));

const MessageInput = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  backgroundColor: "white",
}));

const InputField = styled(TextField)(({ theme }) => ({
  flex: 1,
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(3),
  },
}));

const OnlineIndicator = styled(CircleIcon)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.success.main,
}));

const TemplateMessageCard = styled(Paper)(({ theme }) => ({
  backgroundColor: "#FFF9E6",
  border: "2px dashed #FFC107",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  margin: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
  maxWidth: "500px",
  alignSelf: "center",
}));

const TemplateButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
  color: "white",
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.spacing(3),
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(37, 211, 102, 0.3)",
  "&:hover": {
    background: "linear-gradient(135deg, #128C7E 0%, #0A5D4F 100%)",
    boxShadow: "0 6px 16px rgba(37, 211, 102, 0.4)",
    transform: "translateY(-2px)",
  },
  "&:disabled": {
    background: theme.palette.grey[300],
    color: theme.palette.grey[500],
  },
}));

const AttachmentPreview = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: "#fafafa",
  overflowX: "auto",
  maxHeight: "120px",
}));

const AttachmentCard = styled(Paper)(({ theme }) => ({
  position: "relative",
  width: "80px",
  height: "80px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(0.5),
  borderRadius: theme.spacing(1),
  cursor: "pointer",
  transition: "all 0.2s",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: theme.shadows[4],
  },
}));

const AttachmentRemoveButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: -8,
  right: -8,
  width: 20,
  height: 20,
  padding: 0,
  backgroundColor: theme.palette.error.main,
  color: "white",
  "&:hover": {
    backgroundColor: theme.palette.error.dark,
  },
}));

const MessageAttachment = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  maxWidth: "100%",
}));

const FileAttachmentCard = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  transition: "all 0.2s",
}));

const AudioRecordingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 2),
  backgroundColor: "#f5f5f5",
  borderRadius: theme.spacing(3),
  animation: "pulse 1.5s ease-in-out infinite",
  "@keyframes pulse": {
    "0%, 100%": {
      backgroundColor: "#f5f5f5",
    },
    "50%": {
      backgroundColor: "#ffe0e0",
    },
  },
}));

const WaveAnimation = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "3px",
  height: "24px",
  "& .wave-bar": {
    width: "3px",
    backgroundColor: "#d32f2f",
    borderRadius: "3px",
    animation: "wave 1s ease-in-out infinite",
  },
  "& .wave-bar:nth-of-type(1)": {
    animationDelay: "0s",
  },
  "& .wave-bar:nth-of-type(2)": {
    animationDelay: "0.1s",
  },
  "& .wave-bar:nth-of-type(3)": {
    animationDelay: "0.2s",
  },
  "& .wave-bar:nth-of-type(4)": {
    animationDelay: "0.3s",
  },
  "& .wave-bar:nth-of-type(5)": {
    animationDelay: "0.2s",
  },
  "& .wave-bar:nth-of-type(6)": {
    animationDelay: "0.1s",
  },
  "@keyframes wave": {
    "0%, 100%": {
      height: "8px",
    },
    "50%": {
      height: "24px",
    },
  },
}));

type ModalChatProps = {
  onClose: () => void;
  open: boolean;
  title: string;
  processing: boolean;
};

const ModalChat = ({ onClose, open, title, processing }: ModalChatProps) => {
  // Log de abertura/fechamento do modal
  useEffect(() => {
    if (open) {
      console.log("🔵 ModalChat aberto");
    } else {
      console.log("🔴 ModalChat fechado");
    }
  }, [open]);
  const {
    sessions,
    activeSession,
    messages,
    loading,
    error,
    setActiveSession,
    sendMessage,
    currentUser,
    searchSessions,
    loadMessages,
    loadUserSessions,
    clearError,
    startVideoCall,
  } = useChat();

  const [messageInput, setMessageInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredSessions, setFilteredSessions] = useState(sessions);
  const [conversationsTab, setConversationsTab] = useState<0 | 1>(0);
  const [externalSubTab, setExternalSubTab] = useState<0 | 1>(0);
  const [newChatModalOpen, setNewChatModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [templateSent, setTemplateSent] = useState(false);
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLButtonElement | null>(
    null,
  );
  const [candidateInfo, setCandidateInfo] = useState<any>(null);
  const [loadingCandidateInfo, setLoadingCandidateInfo] = useState(false);
  const [forwardModalOpen, setForwardModalOpen] = useState(false);

  const hasTemplateMessage = messages.some((message) => {
    try {
      if (!message.metadata) return false;

      let metadata = message.metadata;
      if (typeof metadata === "string") {
        metadata = JSON.parse(metadata);
        if (typeof metadata === "string") {
          metadata = JSON.parse(metadata);
        }
      }

      const isTemplate = metadata.template_name === "primeiro_contato";
      if (isTemplate) {
        console.log("✅ Template message found in history!");
      }
      return isTemplate;
    } catch (error) {
      console.log("Error parsing metadata:", error, message.metadata);
      return false;
    }
  });

  const hasMessageFromCandidate = messages.some(
    (message) => message.sender_id !== currentUser?.id,
  );

  const shouldShowTemplate =
    activeSession?.session_type === "external" &&
    !templateSent &&
    !hasTemplateMessage &&
    !hasMessageFromCandidate;

  console.log("Debug - shouldShowTemplate:", {
    isExternal: activeSession?.session_type === "external",
    templateSent,
    hasTemplateMessage,
    hasMessageFromCandidate,
    shouldShowTemplate,
    messageCount: messages.length,
  });
  const [infoTabValue, setInfoTabValue] = useState(0);
  const [removingParticipantId, setRemovingParticipantId] = useState<
    number | null
  >(null);
  const [groupSettingsOpen, setGroupSettingsOpen] = useState(false);
  const [editedGroupName, setEditedGroupName] = useState("");
  const [selectedGroupAvatar, setSelectedGroupAvatar] = useState<File | null>(
    null,
  );
  const [previewGroupAvatar, setPreviewGroupAvatar] = useState<string | null>(
    null,
  );
  const [savingGroupSettings, setSavingGroupSettings] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLButtonElement | null>(
    null,
  );
  const [emojiCategory, setEmojiCategory] = useState(0);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const [emojiSearch, setEmojiSearch] = useState("");
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const [audioRecordingTime, setAudioRecordingTime] = useState(0);
  const audioRecordingInterval = useRef<NodeJS.Timeout | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioStreamRef = useRef<MediaStream | null>(null);

  // Refs para scroll
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevMessagesLength = useRef(0);
  const isLoadingOlder = useRef(false);
  const wasAtBottom = useRef(true);

  // Função para verificar se o scroll está próximo do bottom
  const isNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;

    const threshold = 150; // pixels de tolerância
    const position = container.scrollTop + container.clientHeight;
    const bottom = container.scrollHeight;

    return bottom - position < threshold;
  }, []);

  // Função para rolar até o final
  const scrollToBottom = useCallback((forceInstant = false) => {
    setTimeout(
      () => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({
            behavior: forceInstant ? "auto" : "smooth",
            block: "end",
          });
        }
        wasAtBottom.current = true;
      },
      forceInstant ? 150 : 100,
    );
  }, []);

  // Reset pagination quando mudar de sessão
  useEffect(() => {
    if (activeSession?.id) {
      setLoadingMessages(true); // Marca como carregando ao trocar de sessão
      setCurrentPage(1);
      setHasMoreMessages(true);
      prevMessagesLength.current = 0;
      isLoadingOlder.current = false;
      wasAtBottom.current = true; // Sempre rola ao trocar de sessão
      setTemplateSent(false); // Reset template sent flag

      // Scroll forçado ao abrir uma conversa (após as mensagens carregarem)
      setTimeout(() => {
        scrollToBottom(true);
      }, 300);
    }
  }, [activeSession?.id, scrollToBottom]);

  // Scroll to bottom quando mensagens carregam
  useEffect(() => {
    if (messages.length > 0) {
      setLoadingMessages(false); // Desativa loading quando mensagens carregam

      if (isLoadingOlder.current) {
        isLoadingOlder.current = false;
        return;
      }

      // Se é a primeira carga, rolar imediatamente até o final
      if (prevMessagesLength.current === 0) {
        scrollToBottom(true); // Força scroll instantâneo na primeira carga
      } else if (
        messages.length > prevMessagesLength.current &&
        wasAtBottom.current
      ) {
        scrollToBottom(false); // Scroll suave para novas mensagens
      }

      prevMessagesLength.current = messages.length;
    }
  }, [messages, scrollToBottom]);

  // Desativar loading após timeout se não houver mensagens (conversa nova)
  useEffect(() => {
    if (loadingMessages && activeSession?.id) {
      const timer = setTimeout(() => {
        setLoadingMessages(false);
      }, 1500); // 1.5 segundos de timeout

      return () => clearTimeout(timer);
    }
  }, [loadingMessages, activeSession?.id]);

  // Atualizar estado de "estava no bottom" durante scroll
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScrollCheck = () => {
      wasAtBottom.current = isNearBottom();
    };

    container.addEventListener("scroll", handleScrollCheck);
    return () => container.removeEventListener("scroll", handleScrollCheck);
  }, [isNearBottom]);

  const loadMoreMessages = useCallback(async () => {
    if (!activeSession || loadingMore || !hasMoreMessages) return;

    setLoadingMore(true);
    isLoadingOlder.current = true;

    const nextPage = currentPage + 1;

    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}api/chat/sessions/${activeSession.id}/messages?page=${nextPage}&limit=50`;
      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("rh-token")}`,
        },
      });

      if (response.ok) {
        const olderMessages = await response.json();

        if (olderMessages.length === 0) {
          setHasMoreMessages(false);
        } else {
          const container = messagesContainerRef.current;
          const previousScrollHeight = container?.scrollHeight || 0;

          await loadMessages(activeSession.id, nextPage);

          setCurrentPage(nextPage);

          setTimeout(() => {
            if (container) {
              const newScrollHeight = container.scrollHeight;
              container.scrollTop = newScrollHeight - previousScrollHeight;
            }
          }, 50);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar mais mensagens:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [activeSession, loadingMore, hasMoreMessages, currentPage, loadMessages]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;

      if (target.scrollTop < 100 && hasMoreMessages && !loadingMore) {
        loadMoreMessages();
      }
    },
    [hasMoreMessages, loadingMore, loadMoreMessages],
  );

  const handleSendMessage = async () => {
    if (messageInput.trim() && activeSession) {
      try {
        await sendMessage(messageInput.trim());
        setMessageInput("");
      } catch (error) {
        console.error("Erro ao enviar mensagem:", error);
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendTemplateMessage = async () => {
    if (!activeSession || sendingTemplate) return;

    setSendingTemplate(true);
    try {
      const apiUrl =
        process.env.REACT_APP_API_URL + "api/whatsapp/send/template";

      // Nome do candidato para personalizar o template
      const candidateName = activeSession.candidate_name || "Candidato";

      const response = await api(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_id: activeSession.id,
          phone_number:
            activeSession.phone_number || activeSession.external_identifier,
          template_name: "primeiro_contato",
          language_code: "pt_BR",
          components: [
            {
              type: "body",
              parameters: [
                {
                  type: "text",
                  text: candidateName,
                },
              ],
            },
          ],
        }),
      });

      if (response.ok) {
        setTemplateSent(true);
        // Recarregar mensagens após enviar template
        await loadMessages(activeSession.id, 1);
      } else {
        const errorData = await response.json();
        console.error("Erro ao enviar template:", errorData);
        alert(
          "Erro ao enviar mensagem de template. Por favor, tente novamente.",
        );
      }
    } catch (error) {
      console.error("Erro ao enviar template:", error);
      alert("Erro ao enviar mensagem de template. Por favor, tente novamente.");
    } finally {
      setSendingTemplate(false);
    }
  };

  const handleOpenInfo = async (event: React.MouseEvent<HTMLButtonElement>) => {
    setInfoAnchorEl(event.currentTarget);

    // Se for sessão externa, buscar informações do candidato
    if (
      activeSession?.session_type === "external" &&
      activeSession.candidate_id
    ) {
      setLoadingCandidateInfo(true);
      try {
        const apiUrl =
          process.env.REACT_APP_API_URL +
          `api/candidate/info/${activeSession.candidate_id}`;
        const response = await api(apiUrl);

        if (response.ok) {
          const data = await response.json();
          setCandidateInfo(data);
        } else {
          console.error("Erro ao buscar candidato:", response.status);
        }
      } catch (error) {
        console.error("Erro ao buscar informações do candidato:", error);
      } finally {
        setLoadingCandidateInfo(false);
      }
    }
  };

  const handleCloseInfo = () => {
    setInfoAnchorEl(null);
    setCandidateInfo(null);
    setInfoTabValue(0);
  };

  const handleOpenCandidateProfile = () => {
    if (activeSession?.candidate_id) {
      window.open(`/admin/candidate/${activeSession.candidate_id}`, "_blank");
    }
  };

  const handleRemoveParticipant = async (userId: number, userName: string) => {
    if (!activeSession) return;

    const confirmRemove = window.confirm(
      `Tem certeza que deseja remover ${userName} do grupo?`,
    );

    if (!confirmRemove) return;

    setRemovingParticipantId(userId);

    try {
      const apiUrl =
        process.env.REACT_APP_API_URL +
        `api/chat/sessions/${activeSession.id}/participants/${userId}`;

      const response = await api(apiUrl, {
        method: "DELETE",
      });

      if (response.ok) {
        // Atualizar a sessão ativa removendo o participante localmente
        if (activeSession.participants) {
          const updatedParticipants = activeSession.participants.filter(
            (p: any) => p.user_id !== userId,
          );
          setActiveSession({
            ...activeSession,
            participants: updatedParticipants,
          });
        }

        // Fechar o popover se ainda estiver aberto
        if (infoAnchorEl) {
          handleCloseInfo();
        }
      } else {
        const errorData = await response.json();
        alert(errorData.message || "Erro ao remover participante");
      }
    } catch (error) {
      console.error("Erro ao remover participante:", error);
      alert("Erro ao remover participante do grupo");
    } finally {
      setRemovingParticipantId(null);
    }
  };

  const handleOpenGroupSettings = () => {
    if (!activeSession) return;
    setEditedGroupName(activeSession.session_name || "");
    setPreviewGroupAvatar(activeSession.session_avatar || null);
    setSelectedGroupAvatar(null);
    setGroupSettingsOpen(true);
  };

  const handleCloseGroupSettings = () => {
    setGroupSettingsOpen(false);
    setEditedGroupName("");
    setSelectedGroupAvatar(null);
    setPreviewGroupAvatar(null);
  };

  const handleGroupAvatarChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedGroupAvatar(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewGroupAvatar(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveGroupSettings = async () => {
    if (!activeSession) return;

    setSavingGroupSettings(true);

    try {
      let avatarUrl = activeSession.session_avatar;

      // Se tiver uma nova foto selecionada, fazer upload para S3
      if (selectedGroupAvatar) {
        const formData = new FormData();
        formData.append("file", selectedGroupAvatar);
        formData.append("pathS3", `chat/groups/${activeSession.id}/`);

        const uploadUrl = process.env.REACT_APP_API_URL + "api/upload";
        const uploadResponse = await fetch(uploadUrl, {
          method: "POST",
          body: formData,
        });

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          avatarUrl = `https://agnesrh-images.s3.amazonaws.com/${uploadData.name}`;
        } else {
          throw new Error("Erro ao fazer upload da imagem");
        }
      }

      // Atualizar configurações do grupo
      const updateUrl =
        process.env.REACT_APP_API_URL +
        `api/chat/sessions/${activeSession.id}/settings`;

      const response = await api(updateUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_name: editedGroupName,
          session_avatar: avatarUrl,
        }),
      });

      if (response.ok) {
        const updatedGroup = await response.json();

        // Atualizar a sessão ativa localmente
        setActiveSession({
          ...activeSession,
          session_name: updatedGroup.session_name,
          session_avatar: updatedGroup.session_avatar,
        });

        handleCloseGroupSettings();
      } else {
        const errorData = await response.json();
        alert(errorData.message || "Erro ao atualizar configurações do grupo");
      }
    } catch (error) {
      console.error("Erro ao salvar configurações do grupo:", error);
      alert("Erro ao salvar configurações do grupo");
    } finally {
      setSavingGroupSettings(false);
    }
  };

  const handleAttachFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
    }
    // Limpar input para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendWithAttachments = async () => {
    if (!activeSession) return;

    setUploadingFiles(true);

    try {
      let attachments: any[] = [];

      // Se tiver arquivos selecionados, fazer upload
      if (selectedFiles.length > 0) {
        const formData = new FormData();
        selectedFiles.forEach((file) => {
          formData.append("files", file);
        });
        formData.append("pathS3", `chat/attachments/${activeSession.id}/`);

        const uploadUrl = process.env.REACT_APP_API_URL + "api/upload/multiple";
        const uploadResponse = await fetch(uploadUrl, {
          method: "POST",
          body: formData,
        });

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          attachments = Array.isArray(uploadData) ? uploadData : [];
        } else {
          throw new Error("Erro ao fazer upload dos arquivos");
        }
      }

      // Enviar mensagem com anexos
      const messageContent = messageInput.trim();
      await sendMessage(messageContent || "📎 Arquivos anexados", {
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      // Limpar estados
      setMessageInput("");
      setSelectedFiles([]);
    } catch (error) {
      console.error("Erro ao enviar mensagem com anexos:", error);
      alert("Erro ao enviar arquivos. Por favor, tente novamente.");
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleDownloadFile = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Erro ao baixar arquivo:", error);
      window.open(url, "_blank");
    }
  };

  const handleDownloadAllFiles = (attachments: any[]) => {
    attachments.forEach((file, index) => {
      setTimeout(() => {
        handleDownloadFile(file.url, file.name);
      }, index * 500);
    });
  };

  const emojiCategories = [
    {
      name: "Sorrisos",
      emojis: [
        { emoji: "😀", keywords: "feliz sorriso alegre" },
        { emoji: "😃", keywords: "feliz sorriso alegre" },
        { emoji: "😄", keywords: "feliz sorriso alegre" },
        { emoji: "😁", keywords: "feliz sorriso alegre" },
        { emoji: "😆", keywords: "rindo feliz alegre" },
        { emoji: "😅", keywords: "rindo nervoso suor" },
        { emoji: "🤣", keywords: "rindo gargalhada" },
        { emoji: "😂", keywords: "rindo chorando lágrima" },
        { emoji: "🙂", keywords: "sorriso leve" },
        { emoji: "🙃", keywords: "sorriso invertido" },
        { emoji: "😉", keywords: "piscada flerte" },
        { emoji: "😊", keywords: "sorriso feliz" },
        { emoji: "😇", keywords: "anjo inocente" },
        { emoji: "🥰", keywords: "amor coração apaixonado" },
        { emoji: "😍", keywords: "amor coração apaixonado" },
        { emoji: "🤩", keywords: "estrela impressionado" },
        { emoji: "😘", keywords: "beijo amor" },
        { emoji: "😗", keywords: "beijo" },
        { emoji: "😚", keywords: "beijo" },
        { emoji: "😙", keywords: "beijo" },
        { emoji: "🥲", keywords: "lágrima feliz emocionado" },
      ],
    },
    {
      name: "Gestos",
      emojis: [
        { emoji: "👋", keywords: "tchau oi olá acenar" },
        { emoji: "🤚", keywords: "mão pare" },
        { emoji: "🖐", keywords: "mão cinco" },
        { emoji: "✋", keywords: "mão pare" },
        { emoji: "🖖", keywords: "vulcano star trek" },
        { emoji: "👌", keywords: "ok legal perfeito" },
        { emoji: "🤌", keywords: "italiano mão" },
        { emoji: "🤏", keywords: "pequeno pouco" },
        { emoji: "✌️", keywords: "paz vitória" },
        { emoji: "🤞", keywords: "dedos cruzados sorte" },
        { emoji: "🤟", keywords: "amor rock" },
        { emoji: "🤘", keywords: "rock metal" },
        { emoji: "🤙", keywords: "ligar telefone" },
        { emoji: "👈", keywords: "esquerda apontar" },
        { emoji: "👉", keywords: "direita apontar" },
        { emoji: "👆", keywords: "cima apontar" },
        { emoji: "🖕", keywords: "meio dedo" },
        { emoji: "👇", keywords: "baixo apontar" },
        { emoji: "☝️", keywords: "cima um primeiro" },
        { emoji: "👍", keywords: "joinha legal positivo" },
        { emoji: "👎", keywords: "ruim negativo" },
        { emoji: "✊", keywords: "punho força" },
        { emoji: "👊", keywords: "soco força" },
        { emoji: "🤛", keywords: "soco esquerda" },
        { emoji: "🤜", keywords: "soco direita" },
        { emoji: "👏", keywords: "palmas aplauso parabéns" },
        { emoji: "🙌", keywords: "celebrar comemorar" },
        { emoji: "👐", keywords: "mãos abertas" },
        { emoji: "🤲", keywords: "mãos rezar pedir" },
        { emoji: "🤝", keywords: "aperto mão acordo" },
        { emoji: "🙏", keywords: "rezar obrigado agradecer" },
      ],
    },
    {
      name: "Objetos",
      emojis: [
        { emoji: "📱", keywords: "celular telefone mobile" },
        { emoji: "💻", keywords: "computador notebook laptop" },
        { emoji: "⌨️", keywords: "teclado" },
        { emoji: "🖥", keywords: "computador desktop" },
        { emoji: "🖨", keywords: "impressora" },
        { emoji: "🖱", keywords: "mouse" },
        { emoji: "📁", keywords: "pasta arquivo" },
        { emoji: "📂", keywords: "pasta arquivo aberta" },
        { emoji: "📄", keywords: "documento página" },
        { emoji: "📃", keywords: "documento página rolo" },
        { emoji: "📋", keywords: "prancheta clipboard" },
        { emoji: "📊", keywords: "gráfico barra" },
        { emoji: "📈", keywords: "gráfico crescimento" },
        { emoji: "📉", keywords: "gráfico queda" },
        { emoji: "📆", keywords: "calendário" },
        { emoji: "📅", keywords: "calendário data" },
        { emoji: "📇", keywords: "cartão contato" },
        { emoji: "📌", keywords: "alfinete fixar" },
        { emoji: "📍", keywords: "localização pin" },
        { emoji: "✂️", keywords: "tesoura cortar" },
        { emoji: "📎", keywords: "clipe anexo" },
        { emoji: "🖇", keywords: "clipe anexo duplo" },
        { emoji: "📏", keywords: "régua medida" },
        { emoji: "📐", keywords: "esquadro triângulo" },
      ],
    },
    {
      name: "Símbolos",
      emojis: [
        { emoji: "❤️", keywords: "coração amor vermelho" },
        { emoji: "🧡", keywords: "coração amor laranja" },
        { emoji: "💛", keywords: "coração amor amarelo" },
        { emoji: "💚", keywords: "coração amor verde" },
        { emoji: "💙", keywords: "coração amor azul" },
        { emoji: "💜", keywords: "coração amor roxo" },
        { emoji: "🖤", keywords: "coração amor preto" },
        { emoji: "🤍", keywords: "coração amor branco" },
        { emoji: "🤎", keywords: "coração amor marrom" },
        { emoji: "💔", keywords: "coração partido triste" },
        { emoji: "❣️", keywords: "coração exclamação" },
        { emoji: "💕", keywords: "coração duplo amor" },
        { emoji: "💞", keywords: "coração girando amor" },
        { emoji: "💓", keywords: "coração batendo amor" },
        { emoji: "💗", keywords: "coração crescendo amor" },
        { emoji: "💖", keywords: "coração brilhante amor" },
        { emoji: "💘", keywords: "coração flecha cupido" },
        { emoji: "💝", keywords: "coração presente caixa" },
        { emoji: "✨", keywords: "brilho estrela sparkle" },
        { emoji: "⭐", keywords: "estrela favorito" },
        { emoji: "🌟", keywords: "estrela brilhante" },
        { emoji: "💫", keywords: "estrela tonto" },
        { emoji: "✔️", keywords: "check correto ok" },
        { emoji: "✅", keywords: "check correto ok feito" },
        { emoji: "❌", keywords: "x errado não" },
        { emoji: "⚠️", keywords: "aviso alerta atenção" },
        { emoji: "🔥", keywords: "fogo quente legal" },
        { emoji: "💯", keywords: "cem perfeito total" },
      ],
    },
  ];

  const handleOpenEmoji = (event: React.MouseEvent<HTMLButtonElement>) => {
    setEmojiAnchorEl(event.currentTarget);
  };

  const handleCloseEmoji = () => {
    setEmojiAnchorEl(null);
    setEmojiCategory(0);
    setEmojiSearch("");
  };

  const handleEmojiSelect = (emoji: string) => {
    setMessageInput((prev) => prev + emoji);
    setEmojiSearch("");
    handleCloseEmoji();
    messageInputRef.current?.focus();
  };

  const getFilteredEmojis = () => {
    if (!emojiSearch.trim()) {
      return emojiCategories[emojiCategory].emojis;
    }

    const searchLower = emojiSearch.toLowerCase();
    const allEmojis = emojiCategories.flatMap((cat) => cat.emojis);
    return allEmojis.filter((item) =>
      item.keywords.toLowerCase().includes(searchLower),
    );
  };

  const handleStartAudioRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        await uploadAndSendAudio(audioBlob);
        stopAudioStream();
      };

      mediaRecorder.start();
      setIsRecordingAudio(true);
      setAudioRecordingTime(0);

      audioRecordingInterval.current = setInterval(() => {
        setAudioRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Erro ao acessar microfone:", error);
      alert("Não foi possível acessar o microfone. Verifique as permissões.");
    }
  };

  const stopAudioStream = () => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }
  };

  const handleStopAudioRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }

    setIsRecordingAudio(false);
    setAudioRecordingTime(0);

    if (audioRecordingInterval.current) {
      clearInterval(audioRecordingInterval.current);
      audioRecordingInterval.current = null;
    }
  };

  const uploadAndSendAudio = async (audioBlob: Blob) => {
    if (!activeSession) {
      stopAudioStream();
      return;
    }

    try {
      const formData = new FormData();
      const fileName = `audio_${Date.now()}.webm`;
      formData.append("files", audioBlob, fileName);

      const uploadUrl = process.env.REACT_APP_API_URL + "api/upload/multiple";
      const response = await api(uploadUrl, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const data = await response.json();
      const audioFile = Array.isArray(data) ? data[0] : null;

      if (audioFile) {
        if (activeSession.session_type === "external") {
          const whatsappUrl =
            process.env.REACT_APP_API_URL + "api/whatsapp/send/media";
          const whatsappResponse = await api(whatsappUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              session_id: activeSession.id,
              phone_number:
                activeSession.phone_number || activeSession.external_identifier,
              media_type: "audio",
              media_url: audioFile.url,
            }),
          });

          if (!whatsappResponse.ok) {
            throw new Error("Erro ao enviar áudio via WhatsApp");
          }

          await loadMessages(activeSession.id, 1);
        } else {
          sendMessage(audioFile.url, {
            type: "audio",
          });
        }
      }
    } catch (error) {
      console.error("Erro ao fazer upload do áudio:", error);
      alert("Erro ao enviar áudio. Tente novamente.");
      stopAudioStream();
    }
  };

  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    return () => {
      if (audioRecordingInterval.current) {
        clearInterval(audioRecordingInterval.current);
      }
      stopAudioStream();
    };
  }, []);

  const getFileIcon = (type: string) => {
    if (type.startsWith("image/")) {
      return null; // Imagens mostram preview
    }
    if (type === "application/pdf") {
      return <PictureAsPdfIcon sx={{ fontSize: 48, color: "#d32f2f" }} />;
    }
    if (
      type.includes("word") ||
      type.includes("document") ||
      type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return <DescriptionIcon sx={{ fontSize: 48, color: "#1976d2" }} />;
    }
    return <InsertDriveFileIcon sx={{ fontSize: 48, color: "#757575" }} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const getSessionDisplayName = (session: any) => {
    if (session.session_type === "external") {
      return (
        session.candidate_name || session.session_name || "Contato externo"
      );
    }
    if (session.session_type === "group") {
      return session.session_name || "Grupo sem nome";
    }

    const otherParticipant = session.participants?.find(
      (p: any) => p.user_id !== currentUser?.id,
    );

    return (
      otherParticipant?.user?.nomecomp ||
      otherParticipant?.user?.nome ||
      otherParticipant?.nomecomp ||
      otherParticipant?.nome ||
      "Usuário"
    );
  };

  // Retorna a URL da foto do usuário: para sessão externa usa candidate/session avatar; para usuário do sistema usa img_usuario
  const getSessionAvatarSrc = (session: any): string | undefined => {
    if (session.session_type === "external") {
      return session.candidate_avatar || session.session_avatar || undefined;
    }

    // Grupos usam session_avatar se disponível
    if (session.session_type === "group") {
      return session.session_avatar || undefined;
    }

    // Para sessões privadas, pegar a foto do outro participante
    const otherParticipant = session.participants?.find(
      (p: any) => p.user_id !== currentUser?.id,
    );

    const img =
      otherParticipant?.img_usuario ?? otherParticipant?.user?.img_usuario;

    return img || undefined;
  };

  const hasSessionAvatar = (session: any) => !!getSessionAvatarSrc(session);

  const getSessionIcon = (session: any) => {
    if (session.session_type === "external") {
      // Se não tiver avatar, mostra ícone Person
      if (!hasSessionAvatar(session)) {
        return <PersonIcon sx={{ color: "white" }} />;
      }
      // Se tiver avatar, retorna null para mostrar a foto
      return null;
    }
    if (session.session_type === "group") {
      return <GroupsIcon sx={{ color: "white" }} />;
    }
    // Para sessões privadas sem foto, mostrar inicial do nome
    return session.participants?.[0]?.user?.nome?.charAt(0) || "U";
  };

  const getLastMessageTime = (timestamp: string | undefined) => {
    if (!timestamp) return "";
    return format(new Date(timestamp), "HH:mm", { locale: ptBR });
  };

  useEffect(() => {
    if (searchQuery.trim()) {
      setFilteredSessions(searchSessions(searchQuery));
    } else {
      setFilteredSessions(sessions);
    }
  }, [searchQuery, sessions, searchSessions]);

  const externalSessions = useMemo(
    () => filteredSessions.filter((s) => s.session_type === "external"),
    [filteredSessions],
  );
  const internalSessions = useMemo(
    () =>
      filteredSessions.filter(
        (s) => s.session_type === "private" || s.session_type === "group",
      ),
    [filteredSessions],
  );
  const receptionSessions = useMemo(
    () => externalSessions.filter((s) => !s.attendant_id),
    [externalSessions],
  );
  const myAttendancesSessions = useMemo(
    () =>
      externalSessions.filter(
        (s) => s.attendant_id != null && s.attendant_id === currentUser?.id,
      ),
    [externalSessions, currentUser?.id],
  );
  const sessionsByTab =
    conversationsTab === 0
      ? externalSubTab === 0
        ? receptionSessions
        : myAttendancesSessions
      : internalSessions;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      aria-labelledby="chat-dialog-title"
      fullScreen
      maxWidth="xl"
      TransitionComponent={Fade as any}
      transitionDuration={300}
    >
      <HeaderBar>
        <TitleSection>
          <ChatIconWrapper>
            <ChatRoundedIcon sx={{ color: "#fff", fontSize: "20px" }} />
          </ChatIconWrapper>
          <Box>
            <HeaderTitle>Chat</HeaderTitle>
            <HeaderSubtitle>
              {sessions.length} conversa{sessions.length !== 1 ? "s" : ""}
            </HeaderSubtitle>
          </Box>
        </TitleSection>

        <ControlsSection>
          <Tooltip title="Nova Conversa" arrow>
            <AddButton size="small" onClick={() => setNewChatModalOpen(true)}>
              <AddRoundedIcon sx={{ fontSize: "18px" }} />
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#fff",
                  display: { xs: "none", sm: "block" },
                }}
              >
                Nova Conversa
              </Typography>
            </AddButton>
          </Tooltip>
          <Tooltip title="Fechar" arrow>
            <CloseButton size="small" onClick={onClose}>
              <CloseRoundedIcon sx={{ fontSize: "20px" }} />
            </CloseButton>
          </Tooltip>
        </ControlsSection>
      </HeaderBar>

      <StyledDialogContent>
        {/* Sidebar com lista de conversas */}
        <Sidebar>
          {/* <SidebarHeader>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" fontWeight="bold">
                Conversas
              </Typography>
              {loading && <CircularProgress size={20} />}
            </Box>
          </SidebarHeader> */}

          <ConversationsTabsWrapper>
            <Tabs
              value={conversationsTab}
              onChange={(_, v: 0 | 1) => setConversationsTab(v)}
              variant="fullWidth"
              sx={{ flex: 1 }}
            >
              <Tab
                label={
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <WhatsAppIcon
                      sx={{ fontSize: 20, color: "#25D366" }}
                      className="tab-icon-external"
                    />
                    <span>WhatsApp</span>
                    <Chip
                      size="small"
                      label={externalSessions.length}
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <GroupsIcon
                      sx={{ fontSize: 20, color: "#1976d2" }}
                      className="tab-icon-internal"
                    />
                    <span>Interno</span>
                    <Chip
                      size="small"
                      label={internalSessions.length}
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  </Box>
                }
              />
            </Tabs>
          </ConversationsTabsWrapper>

          {conversationsTab === 0 && (
            <ExternalSubTabsWrapper>
              <Tabs
                value={externalSubTab}
                onChange={(_, v: 0 | 1) => setExternalSubTab(v)}
                variant="fullWidth"
                sx={{ flex: 1 }}
              >
                <Tab
                  icon={
                    <Box
                      component="span"
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                    >
                      <InboxIcon sx={{ fontSize: 18 }} />
                      <span>Recepção</span>
                      <Chip
                        size="small"
                        label={receptionSessions.length}
                        sx={{
                          height: 18,
                          fontSize: "0.7rem",
                          "& .MuiChip-label": { px: 0.5 },
                        }}
                      />
                    </Box>
                  }
                  iconPosition="start"
                  sx={{ "& .MuiTab-iconWrapper": { marginRight: 0 } }}
                />
                <Tab
                  icon={
                    <Box
                      component="span"
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                    >
                      <PersonIcon sx={{ fontSize: 18 }} />
                      <span>Atendimentos</span>
                      <Chip
                        size="small"
                        label={myAttendancesSessions.length}
                        sx={{
                          height: 18,
                          fontSize: "0.7rem",
                          "& .MuiChip-label": { px: 0.5 },
                        }}
                      />
                    </Box>
                  }
                  iconPosition="start"
                  sx={{ "& .MuiTab-iconWrapper": { marginRight: 0 } }}
                />
              </Tabs>
            </ExternalSubTabsWrapper>
          )}

          <SearchContainer>
            <TextField
              label="Buscar"
              value={searchQuery}
              size="small"
              variant="outlined"
              fullWidth
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchRoundedIcon sx={{ mr: 1, color: "grey.500" }} />
                ),
              }}
            />
            <Tooltip title="Nova conversa">
              <IconButton
                size="small"
                onClick={() => setNewChatModalOpen(true)}
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                }}
              >
                <AddRoundedIcon />
              </IconButton>
            </Tooltip>
          </SearchContainer>

          <ChatList>
            {error && (
              <Box p={2}>
                <Alert severity="error">{error}</Alert>
              </Box>
            )}
            {sessionsByTab.map((session) => (
              <ChatListItem
                key={session.id}
                className={activeSession?.id === session.id ? "active" : ""}
                onClick={() => setActiveSession(session)}
              >
                <ListItemAvatar>
                  <Avatar
                    src={getSessionAvatarSrc(session)}
                    sx={{
                      bgcolor:
                        session.session_type === "external"
                          ? "#25D366"
                          : session.session_type === "group"
                            ? "#1976d2"
                            : "#7b1fa2",
                    }}
                  >
                    {!hasSessionAvatar(session) && getSessionIcon(session)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                      gap={1}
                      overflow="hidden"
                    >
                      {session.session_type === "external" && (
                        <Chip
                          label="WhatsApp"
                          size="small"
                          sx={{
                            bgcolor: "#25D366",
                            color: "white",
                            fontSize: "0.65rem",
                            height: 18,
                          }}
                        />
                      )}
                      {session.session_type === "group" && (
                        <Chip
                          label="Grupo"
                          size="small"
                          sx={{
                            bgcolor: "#1976d2",
                            color: "white",
                            fontSize: "0.65rem",
                            height: 18,
                          }}
                        />
                      )}
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        width="100%"
                        overflow="hidden"
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight="medium"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {session.session_type === "private" && (
                            <OnlineIndicator sx={{ flexShrink: 0, mr: 0.5 }} />
                          )}
                          {getSessionDisplayName(session)}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {session.session_type === "external"
                          ? session.phone_number || "Contato externo"
                          : "Última mensagem..."}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="caption" color="text.secondary">
                          {getLastMessageTime(session.last_message_at)}
                        </Typography>
                        {(session.unread_count ?? 0) > 0 && (
                          <Chip
                            size="small"
                            label={session.unread_count}
                            color="primary"
                            sx={{ height: 20, fontSize: "0.75rem" }}
                          />
                        )}
                      </Box>
                    </Box>
                  }
                />
              </ChatListItem>
            ))}
          </ChatList>
        </Sidebar>

        {/* Área principal do chat */}
        <ChatArea>
          {activeSession ? (
            <>
              {/* Header do chat */}
              <ChatHeader>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar
                    src={getSessionAvatarSrc(activeSession)}
                    sx={{
                      bgcolor:
                        activeSession.session_type === "external"
                          ? "#25D366"
                          : activeSession.session_type === "group"
                            ? "#1976d2"
                            : "#7b1fa2",
                      width: 48,
                      height: 48,
                    }}
                  >
                    {!hasSessionAvatar(activeSession) &&
                      getSessionIcon(activeSession)}
                  </Avatar>
                  <Box overflow="hidden" flex={1} minWidth={0}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      overflow="hidden"
                    >
                      <Typography
                        variant="h6"
                        fontWeight="medium"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {getSessionDisplayName(activeSession)}
                      </Typography>
                      {activeSession.session_type === "external" && (
                        <Chip
                          label="WhatsApp"
                          size="small"
                          sx={{
                            bgcolor: "#25D366",
                            color: "white",
                            fontSize: "0.7rem",
                            height: 20,
                            flexShrink: 0,
                          }}
                        />
                      )}
                      {activeSession.session_type === "group" && (
                        <Chip
                          label="Grupo"
                          size="small"
                          sx={{
                            bgcolor: "#1976d2",
                            color: "white",
                            fontSize: "0.7rem",
                            height: 20,
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      overflow="hidden"
                    >
                      {activeSession.session_type === "external" ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {activeSession.phone_number || "Contato externo"}
                        </Typography>
                      ) : activeSession.session_type === "private" ? (
                        <>
                          <OnlineIndicator sx={{ flexShrink: 0 }} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            Online
                          </Typography>
                        </>
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {activeSession.participants?.length || 0}{" "}
                          participantes
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  {activeSession.session_type !== "external" && (
                    <>
                      {/*  <Tooltip title="Chamada de voz">
                        <IconButton>
                          <PhoneRoundedIcon />
                        </IconButton>
                      </Tooltip> */}
                      <Tooltip title="Chamada de vídeo">
                        <IconButton
                          onClick={() => startVideoCall(activeSession.id)}
                        >
                          <VideoCallRoundedIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {activeSession.session_type === "group" && (
                    <Tooltip title="Configurações do grupo">
                      <IconButton onClick={handleOpenGroupSettings}>
                        <SettingsIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {activeSession.session_type === "external" && (
                    <Tooltip title="Encaminhar Atendimento">
                      <IconButton
                        size="small"
                        onClick={() => setForwardModalOpen(true)}
                        sx={{ color: "primary.main" }}
                      >
                        <SendRoundedIcon sx={{ fontSize: 22 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Informações">
                    <IconButton onClick={handleOpenInfo}>
                      <InfoRoundedIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Mais opções">
                    <IconButton>
                      <MoreVertRoundedIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ChatHeader>

              {/* Container de mensagens */}
              <MessagesContainer>
                <ChatBackground />

                {/* Loading centralizado ao abrir o chat */}
                {loadingMessages && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    zIndex={2}
                    bgcolor="rgba(255, 255, 255, 0.9)"
                  >
                    <CircularProgress size={40} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2 }}
                    >
                      Carregando mensagens...
                    </Typography>
                  </Box>
                )}

                <MessagesScroll
                  ref={messagesContainerRef}
                  onScroll={handleScroll}
                >
                  {loadingMore && (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </Box>
                  )}

                  <Box display="flex" flexDirection="column" gap={1}>
                    {shouldShowTemplate && (
                      <TemplateMessageCard elevation={0}>
                        <WhatsAppIcon sx={{ fontSize: 48, color: "#25D366" }} />
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          color="text.primary"
                          textAlign="center"
                        >
                          Iniciar Conversa
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          textAlign="center"
                        >
                          Para iniciar a conversa com o candidato via WhatsApp,
                          envie uma mensagem de template aprovada. Isso
                          permitirá que você se comunique com o candidato.
                        </Typography>
                        <TemplateButton
                          variant="contained"
                          onClick={handleSendTemplateMessage}
                          disabled={sendingTemplate}
                          startIcon={
                            sendingTemplate ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <WhatsAppIcon />
                            )
                          }
                        >
                          {sendingTemplate
                            ? "Enviando..."
                            : "Enviar Mensagem de Primeiro Contato"}
                        </TemplateButton>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          textAlign="center"
                          sx={{ fontStyle: "italic" }}
                        >
                          Esta é uma mensagem automatizada e pré-aprovada pelo
                          WhatsApp
                        </Typography>
                      </TemplateMessageCard>
                    )}

                    {messages.map((message) => (
                      <Box
                        key={message.id}
                        display="flex"
                        flexDirection="column"
                        alignItems={
                          message.sender_id === currentUser?.id
                            ? "flex-end"
                            : "flex-start"
                        }
                      >
                        <MessageBubble
                          isOwn={message.sender_id === currentUser?.id}
                          sx={{
                            ...(message.message_type === "audio" && {
                              padding: 0,
                              backgroundColor: "transparent",
                              boxShadow: "none",
                            }),
                          }}
                        >
                          {message.message_type === "audio" &&
                          message.message_content ? (
                            <AudioPlayer
                              audioUrl={message.message_content}
                              isOwnMessage={
                                message.sender_id === currentUser?.id
                              }
                            />
                          ) : (
                            message.message_content && (
                              <Typography variant="body2">
                                {message.message_content}
                              </Typography>
                            )
                          )}

                          {/* Anexos */}
                          {(message as any).attachments &&
                            Array.isArray((message as any).attachments) &&
                            (message as any).attachments.length > 0 && (
                              <MessageAttachment>
                                {(message as any).attachments.map(
                                  (file: any, index: number) => (
                                    <FileAttachmentCard
                                      key={index}
                                      elevation={2}
                                    >
                                      {file.type.startsWith("image/") ? (
                                        <img
                                          src={file.url}
                                          alt={file.name}
                                          style={{
                                            width: "100%",
                                            maxHeight: "150px",
                                            objectFit: "cover",
                                            borderRadius: "8px",
                                            marginBottom: "8px",
                                            cursor: "pointer",
                                          }}
                                          onClick={() =>
                                            handleDownloadFile(
                                              file.url,
                                              file.name,
                                            )
                                          }
                                        />
                                      ) : (
                                        <Box
                                          display="flex"
                                          flexDirection="column"
                                          alignItems="center"
                                          gap={1}
                                        >
                                          {getFileIcon(file.type)}
                                        </Box>
                                      )}
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          textAlign: "center",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: "100%",
                                          display: "block",
                                        }}
                                      >
                                        {file.name}
                                      </Typography>
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={0.5}
                                        justifyContent="center"
                                      >
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ fontSize: "0.7rem" }}
                                        >
                                          {formatFileSize(file.size)}
                                        </Typography>
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleDownloadFile(
                                              file.url,
                                              file.name,
                                            )
                                          }
                                          sx={{
                                            padding: "2px",
                                            "&:hover": {
                                              color: "primary.main",
                                            },
                                          }}
                                        >
                                          <DownloadIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Box>
                                    </FileAttachmentCard>
                                  ),
                                )}
                              </MessageAttachment>
                            )}

                          {/* Botão para baixar todos os arquivos */}
                          {(message as any).attachments &&
                            Array.isArray((message as any).attachments) &&
                            (message as any).attachments.length > 1 && (
                              <Button
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadAllFiles(
                                    (message as any).attachments,
                                  );
                                }}
                                sx={{ mt: 1 }}
                              >
                                Baixar todos (
                                {(message as any).attachments.length})
                              </Button>
                            )}
                        </MessageBubble>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, mx: 1 }}
                        >
                          {message.sender_name || message.sender?.nome} •{" "}
                          {getLastMessageTime(message.sent_at)}
                        </Typography>
                      </Box>
                    ))}
                    <div ref={messagesEndRef} />
                  </Box>
                </MessagesScroll>
              </MessagesContainer>

              {/* Erro de envio de mensagem */}
              {error && (
                <Box px={2} py={1}>
                  <Alert severity="error" onClose={clearError}>
                    {error}
                  </Alert>
                </Box>
              )}

              {/* Preview de arquivos selecionados */}
              {selectedFiles.length > 0 && (
                <AttachmentPreview>
                  {selectedFiles.map((file, index) => (
                    <AttachmentCard key={index} elevation={2}>
                      <AttachmentRemoveButton
                        size="small"
                        onClick={() => handleRemoveFile(index)}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </AttachmentRemoveButton>
                      {file.type.startsWith("image/") ? (
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "8px",
                          }}
                        />
                      ) : (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          gap={0.5}
                        >
                          {getFileIcon(file.type)}
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.6rem",
                              textAlign: "center",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "70px",
                            }}
                          >
                            {file.name}
                          </Typography>
                        </Box>
                      )}
                    </AttachmentCard>
                  ))}
                </AttachmentPreview>
              )}

              {/* Input de mensagem */}
              <MessageInput>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                />
                <Tooltip title="Anexar arquivo">
                  <IconButton
                    size="small"
                    onClick={handleAttachFiles}
                    disabled={shouldShowTemplate || uploadingFiles}
                  >
                    <AttachFileRoundedIcon />
                  </IconButton>
                </Tooltip>
                {isRecordingAudio ? (
                  <AudioRecordingContainer sx={{ flex: 1 }}>
                    <WaveAnimation>
                      <div className="wave-bar" />
                      <div className="wave-bar" />
                      <div className="wave-bar" />
                      <div className="wave-bar" />
                      <div className="wave-bar" />
                      <div className="wave-bar" />
                    </WaveAnimation>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: "#d32f2f" }}
                    >
                      {formatRecordingTime(audioRecordingTime)}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Parar gravação">
                      <IconButton
                        size="small"
                        onClick={handleStopAudioRecording}
                        sx={{
                          bgcolor: "#d32f2f",
                          color: "white",
                          "&:hover": { bgcolor: "#c62828" },
                        }}
                      >
                        <StopIcon />
                      </IconButton>
                    </Tooltip>
                  </AudioRecordingContainer>
                ) : (
                  <>
                    <InputField
                      inputRef={messageInputRef}
                      placeholder={
                        shouldShowTemplate
                          ? "Envie a mensagem de template primeiro..."
                          : "Digite sua mensagem..."
                      }
                      multiline
                      maxRows={4}
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      variant="outlined"
                      size="small"
                      disabled={shouldShowTemplate}
                    />
                    <Tooltip title="Gravar áudio">
                      <IconButton
                        size="small"
                        onClick={handleStartAudioRecording}
                        disabled={shouldShowTemplate}
                      >
                        <MicIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Emoji">
                      <IconButton
                        size="small"
                        onClick={handleOpenEmoji}
                        disabled={shouldShowTemplate}
                      >
                        <EmojiEmotionsRoundedIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                <Tooltip title="Enviar">
                  <IconButton
                    sx={{
                      bgcolor: "#7b1fa2",
                      color: "white",
                      "&:hover": { bgcolor: "#6a1b9a" },
                    }}
                    onClick={
                      selectedFiles.length > 0
                        ? handleSendWithAttachments
                        : handleSendMessage
                    }
                    disabled={
                      uploadingFiles ||
                      (!messageInput.trim() && selectedFiles.length === 0) ||
                      shouldShowTemplate
                    }
                  >
                    {uploadingFiles ? (
                      <CircularProgress size={20} sx={{ color: "white" }} />
                    ) : (
                      <SendRoundedIcon />
                    )}
                  </IconButton>
                </Tooltip>
              </MessageInput>
            </>
          ) : (
            // Estado vazio - nenhuma conversa selecionada
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              textAlign="center"
              gap={2}
            >
              <ChatRoundedIcon sx={{ fontSize: 80, color: "grey.300" }} />
              <Typography variant="h5" color="text.secondary">
                Selecione uma conversa
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Escolha uma conversa existente ou inicie uma nova
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={() => setNewChatModalOpen(true)}
                sx={{ bgcolor: "#7b1fa2", "&:hover": { bgcolor: "#6a1b9a" } }}
              >
                Nova Conversa
              </Button>
            </Box>
          )}
        </ChatArea>
      </StyledDialogContent>

      <ModalNewChat
        open={newChatModalOpen}
        onClose={() => setNewChatModalOpen(false)}
      />

      <ModalForwardAttendance
        open={forwardModalOpen}
        onClose={() => setForwardModalOpen(false)}
        sessionId={activeSession?.id ?? 0}
        currentUserId={currentUser?.id}
        onSuccess={async () => {
          await loadUserSessions();
          setActiveSession(null);
        }}
      />

      <Popover
        open={Boolean(emojiAnchorEl)}
        anchorEl={emojiAnchorEl}
        onClose={handleCloseEmoji}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 450,
            overflow: "hidden",
            borderRadius: 2,
            mt: -1,
          },
        }}
      >
        <Box>
          <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Buscar emoji..."
              value={emojiSearch}
              onChange={(e) => setEmojiSearch(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                startAdornment: (
                  <SearchRoundedIcon
                    sx={{ fontSize: 18, mr: 0.5, color: "text.secondary" }}
                  />
                ),
              }}
            />
          </Box>

          {!emojiSearch && (
            <Tabs
              value={emojiCategory}
              onChange={(e, newValue) => setEmojiCategory(newValue)}
              variant="fullWidth"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                minHeight: 40,
              }}
            >
              {emojiCategories.map((category, index) => (
                <Tab
                  key={index}
                  label={category.name}
                  sx={{
                    minHeight: 40,
                    fontSize: "0.75rem",
                    py: 0.5,
                  }}
                />
              ))}
            </Tabs>
          )}

          <Box
            sx={{
              p: 2,
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 1,
              maxHeight: 300,
              overflow: "auto",
            }}
          >
            {getFilteredEmojis().length > 0 ? (
              getFilteredEmojis().map((item, index) => (
                <IconButton
                  key={index}
                  onClick={() => handleEmojiSelect(item.emoji)}
                  sx={{
                    fontSize: "1.5rem",
                    width: 40,
                    height: 40,
                    opacity: 1,
                    "&:hover": {
                      bgcolor: "action.hover",
                      transform: "scale(1.2)",
                    },
                  }}
                >
                  {item.emoji}
                </IconButton>
              ))
            ) : (
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  py: 4,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Nenhum emoji encontrado
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Popover>

      <Dialog
        open={groupSettingsOpen}
        onClose={handleCloseGroupSettings}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SettingsIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Configurações do Grupo
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Preview do avatar */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
            >
              <Avatar
                src={previewGroupAvatar || undefined}
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: "#1976d2",
                  fontSize: "3rem",
                }}
              >
                {!previewGroupAvatar && (
                  <GroupsIcon sx={{ fontSize: "3rem" }} />
                )}
              </Avatar>

              <input
                accept="image/*"
                style={{ display: "none" }}
                id="group-avatar-upload"
                type="file"
                onChange={handleGroupAvatarChange}
              />
              <label htmlFor="group-avatar-upload">
                <Button
                  variant="outlined"
                  component="span"
                  size="small"
                  startIcon={<PhotoCameraIcon />}
                >
                  Alterar Foto do Grupo
                </Button>
              </label>
            </Box>

            {/* Campo de nome do grupo */}
            <TextField
              size="small"
              label="Nome do Grupo"
              fullWidth
              value={editedGroupName}
              onChange={(e) => setEditedGroupName(e.target.value)}
              variant="outlined"
              placeholder="Digite o nome do grupo"
              helperText="Este nome será visível para todos os participantes"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseGroupSettings}
            size="small"
            disabled={savingGroupSettings}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveGroupSettings}
            size="small"
            variant="contained"
            disabled={savingGroupSettings || !editedGroupName.trim()}
            startIcon={
              savingGroupSettings ? <CircularProgress size={16} /> : null
            }
          >
            {savingGroupSettings ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Popover de informações */}
      <Popover
        open={Boolean(infoAnchorEl)}
        anchorEl={infoAnchorEl}
        onClose={handleCloseInfo}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box
          sx={{
            width: activeSession?.session_type === "external" ? 420 : 320,
            maxHeight: 600,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Informações para Grupos */}
          {activeSession?.session_type === "group" && (
            <Box
              sx={{ display: "flex", flexDirection: "column", height: "100%" }}
            >
              <Box sx={{ p: 2, bgcolor: "#1976d2", color: "white" }}>
                <Typography variant="h6" fontWeight="bold">
                  Participantes do Grupo
                </Typography>
                <Typography variant="caption">
                  {activeSession.participants?.length || 0} membros
                </Typography>
              </Box>

              <List sx={{ p: 0, overflow: "auto", flex: 1 }}>
                {activeSession.participants?.map(
                  (participant: any, index: number) => {
                    const isCurrentUser =
                      participant.user_id === currentUser?.id;
                    const canRemove = !isCurrentUser;

                    return (
                      <React.Fragment
                        key={participant.user_id || participant.id}
                      >
                        <ListItem
                          sx={{
                            pr: canRemove ? 7 : 2,
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              src={
                                participant.img_usuario ||
                                participant.user?.img_usuario
                              }
                              sx={{ bgcolor: "#7b1fa2" }}
                            >
                              {(
                                participant.user?.nome ||
                                participant.nome ||
                                "U"
                              ).charAt(0)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body1" fontWeight="medium">
                                  {participant.user?.nomecomp ||
                                    participant.nomecomp ||
                                    participant.user?.nome ||
                                    participant.nome ||
                                    "Usuário"}
                                </Typography>
                                {isCurrentUser && (
                                  <Chip
                                    label="Você"
                                    size="small"
                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              participant.user?.email || participant.email || ""
                            }
                          />
                          {canRemove && (
                            <Tooltip title="Remover do grupo">
                              <IconButton
                                onClick={() =>
                                  handleRemoveParticipant(
                                    participant.user_id,
                                    participant.user?.nomecomp ||
                                      participant.nomecomp ||
                                      participant.user?.nome ||
                                      participant.nome ||
                                      "Usuário",
                                  )
                                }
                                disabled={
                                  removingParticipantId === participant.user_id
                                }
                                sx={{
                                  position: "absolute",
                                  right: 8,
                                  color: "error.main",
                                  "&:hover": {
                                    bgcolor: "error.lighter",
                                  },
                                }}
                                size="small"
                              >
                                {removingParticipantId ===
                                participant.user_id ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <PersonRemoveIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </ListItem>
                        {index <
                          (activeSession.participants?.length || 0) - 1 && (
                          <Divider />
                        )}
                      </React.Fragment>
                    );
                  },
                )}
              </List>
            </Box>
          )}

          {/* Informações para Candidatos Externos */}
          {activeSession?.session_type === "external" && (
            <Box>
              {/* Header com botão para abrir perfil */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#25D366",
                  color: "white",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Informações do Candidato
                  </Typography>
                  {candidateInfo && (
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      ID: {activeSession.candidate_id}
                    </Typography>
                  )}
                </Box>
                {candidateInfo && (
                  <Tooltip title="Abrir perfil completo">
                    <IconButton
                      onClick={handleOpenCandidateProfile}
                      sx={{
                        color: "white",
                        bgcolor: "rgba(255, 255, 255, 0.2)",
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.3)",
                        },
                      }}
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {loadingCandidateInfo ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress size={40} />
                </Box>
              ) : candidateInfo ? (
                <>
                  {/* Tabs */}
                  <Tabs
                    value={infoTabValue}
                    onChange={(_, newValue) => setInfoTabValue(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      bgcolor: "#f5f5f5",
                    }}
                  >
                    <Tab
                      icon={<PersonIcon />}
                      iconPosition="start"
                      label="Pessoal"
                    />
                    <Tab
                      icon={<HomeIcon />}
                      iconPosition="start"
                      label="Endereço"
                    />
                    <Tab
                      icon={<WorkIcon />}
                      iconPosition="start"
                      label="Profissional"
                    />
                    <Tab
                      icon={<SchoolIcon />}
                      iconPosition="start"
                      label="Formação"
                    />
                    <Tab
                      icon={<BadgeIcon />}
                      iconPosition="start"
                      label="Documentos"
                    />
                  </Tabs>

                  {/* Conteúdo das tabs com scroll */}
                  <Box sx={{ overflow: "auto", flex: 1 }}>
                    {/* Tab Pessoal */}
                    {infoTabValue === 0 && (
                      <List sx={{ p: 0 }}>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: "grey.100" }}>
                              <PersonIcon color="primary" />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Nome Completo
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" fontWeight="medium">
                                {candidateInfo.pessoal_nome || "N/A"}
                              </Typography>
                            }
                          />
                        </ListItem>
                        <Divider />

                        {candidateInfo.pessoal_email && (
                          <>
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: "grey.100" }}>
                                  <EmailIcon color="primary" />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Email
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="body2">
                                    {candidateInfo.pessoal_email}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            <Divider />
                          </>
                        )}

                        {candidateInfo.pessoal_telefones &&
                          candidateInfo.pessoal_telefones.length > 0 && (
                            <>
                              <ListItem>
                                <ListItemAvatar>
                                  <Avatar sx={{ bgcolor: "grey.100" }}>
                                    <PhoneIcon color="primary" />
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Telefones
                                    </Typography>
                                  }
                                  secondary={
                                    <Box>
                                      {candidateInfo.pessoal_telefones.map(
                                        (tel: any, idx: number) => (
                                          <Box
                                            key={idx}
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            mt={0.5}
                                          >
                                            <Typography variant="body2">
                                              ({tel.ddd}) {tel.phone}
                                              {tel.name && ` - ${tel.name}`}
                                            </Typography>
                                            {tel.whatsapp === 1 && (
                                              <Chip
                                                label="WhatsApp"
                                                size="small"
                                                sx={{
                                                  bgcolor: "#25D366",
                                                  color: "white",
                                                  height: 18,
                                                  fontSize: "0.65rem",
                                                }}
                                              />
                                            )}
                                          </Box>
                                        ),
                                      )}
                                    </Box>
                                  }
                                />
                              </ListItem>
                              <Divider />
                            </>
                          )}

                        {candidateInfo.idade && (
                          <>
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: "grey.100" }}>
                                  <CalendarTodayIcon color="primary" />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Idade
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="body2">
                                    {candidateInfo.idade} anos
                                  </Typography>
                                }
                              />
                            </ListItem>
                            <Divider />
                          </>
                        )}

                        {candidateInfo.pessoal_sexo && (
                          <>
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: "grey.100" }}>
                                  <PersonIcon color="primary" />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Sexo
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="body2">
                                    {candidateInfo.pessoal_sexo === "M"
                                      ? "Masculino"
                                      : "Feminino"}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            <Divider />
                          </>
                        )}

                        {candidateInfo.pessoal_salario && (
                          <ListItem>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: "grey.100" }}>
                                <AttachMoneyIcon color="primary" />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Pretensão Salarial
                                </Typography>
                              }
                              secondary={
                                <Typography variant="body2">
                                  R${" "}
                                  {candidateInfo.pessoal_salario?.toLocaleString(
                                    "pt-BR",
                                  )}
                                </Typography>
                              }
                            />
                          </ListItem>
                        )}
                      </List>
                    )}

                    {/* Tab Endereço */}
                    {infoTabValue === 1 && (
                      <List sx={{ p: 0 }}>
                        {candidateInfo.pessoal_endereco && (
                          <>
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: "grey.100" }}>
                                  <HomeIcon color="primary" />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Endereço
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="body2">
                                    {candidateInfo.pessoal_endereco}
                                    {candidateInfo.pessoal_numero &&
                                      `, ${candidateInfo.pessoal_numero}`}
                                    {candidateInfo.pessoal_complemento &&
                                      ` - ${candidateInfo.pessoal_complemento}`}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            <Divider />
                          </>
                        )}

                        {candidateInfo.pessoal_bairro && (
                          <>
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: "grey.100" }}>
                                  <LocationOnIcon color="primary" />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Bairro
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="body2">
                                    {candidateInfo.pessoal_bairro}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            <Divider />
                          </>
                        )}

                        {candidateInfo.pessoal_cidade && (
                          <>
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: "grey.100" }}>
                                  <LocationOnIcon color="primary" />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Cidade / UF
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="body2">
                                    {candidateInfo.pessoal_cidade}
                                    {candidateInfo.pessoal_uf &&
                                      ` - ${candidateInfo.pessoal_uf}`}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            <Divider />
                          </>
                        )}

                        {candidateInfo.pessoal_cep &&
                          candidateInfo.pessoal_cep !== "-" && (
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: "grey.100" }}>
                                  <LocationOnIcon color="primary" />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    CEP
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="body2">
                                    {candidateInfo.pessoal_cep}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          )}
                      </List>
                    )}

                    {/* Tab Profissional */}
                    {infoTabValue === 2 && (
                      <Box>
                        {/* Cargos de Interesse */}
                        {candidateInfo.pessoal_cargos &&
                          candidateInfo.pessoal_cargos.length > 0 && (
                            <Box sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                gutterBottom
                              >
                                Cargos de Interesse
                              </Typography>
                              <Box
                                display="flex"
                                flexWrap="wrap"
                                gap={1}
                                mt={1}
                              >
                                {candidateInfo.pessoal_cargos.map(
                                  (cargo: any, idx: number) => (
                                    <Chip
                                      key={idx}
                                      label={cargo.nome}
                                      size="small"
                                      sx={{
                                        bgcolor: "#e3f2fd",
                                        color: "#1976d2",
                                      }}
                                    />
                                  ),
                                )}
                              </Box>
                            </Box>
                          )}

                        <Divider />

                        {/* Experiências Profissionais */}
                        {candidateInfo.pessoal_empregos &&
                          candidateInfo.pessoal_empregos.length > 0 && (
                            <List sx={{ p: 0 }}>
                              <ListItem sx={{ bgcolor: "#fafafa" }}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                >
                                  Experiências Profissionais
                                </Typography>
                              </ListItem>
                              {candidateInfo.pessoal_empregos.map(
                                (emp: any, idx: number) => (
                                  <React.Fragment key={emp.id || idx}>
                                    <ListItem
                                      sx={{
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        py: 2,
                                      }}
                                    >
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                        mb={1}
                                      >
                                        <WorkIcon
                                          color="primary"
                                          sx={{ fontSize: 20 }}
                                        />
                                        <Typography
                                          variant="body2"
                                          fontWeight="bold"
                                        >
                                          {emp.cargo?.nome ||
                                            "Cargo não informado"}
                                        </Typography>
                                        {emp.empatual === 1 && (
                                          <Chip
                                            label="Atual"
                                            size="small"
                                            color="success"
                                            sx={{
                                              height: 20,
                                              fontSize: "0.7rem",
                                            }}
                                          />
                                        )}
                                      </Box>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {emp.empresa}
                                      </Typography>
                                      {emp.salario && (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          Salário: R${" "}
                                          {parseFloat(
                                            emp.salario,
                                          ).toLocaleString("pt-BR")}
                                        </Typography>
                                      )}
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {emp.dt_admissao &&
                                          `De ${new Date(emp.dt_admissao).toLocaleDateString("pt-BR")}`}
                                        {emp.dt_saida
                                          ? ` até ${new Date(emp.dt_saida).toLocaleDateString("pt-BR")}`
                                          : " até o momento"}
                                      </Typography>
                                    </ListItem>
                                    {idx <
                                      candidateInfo.pessoal_empregos.length -
                                        1 && <Divider />}
                                  </React.Fragment>
                                ),
                              )}
                            </List>
                          )}

                        {(!candidateInfo.pessoal_cargos ||
                          candidateInfo.pessoal_cargos.length === 0) &&
                          (!candidateInfo.pessoal_empregos ||
                            candidateInfo.pessoal_empregos.length === 0) && (
                            <Box p={3} textAlign="center">
                              <WorkIcon
                                color="primary"
                                sx={{ fontSize: 48, opacity: 0.3, mb: 1 }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Sem informações profissionais
                              </Typography>
                            </Box>
                          )}
                      </Box>
                    )}

                    {/* Tab Formação */}
                    {infoTabValue === 3 && (
                      <Box>
                        {candidateInfo.pessoal_formacoes &&
                        candidateInfo.pessoal_formacoes.length > 0 ? (
                          <List sx={{ p: 0 }}>
                            {candidateInfo.pessoal_formacoes.map(
                              (form: any, idx: number) => (
                                <React.Fragment key={idx}>
                                  <ListItem
                                    sx={{
                                      flexDirection: "column",
                                      alignItems: "flex-start",
                                      py: 2,
                                    }}
                                  >
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                      mb={1}
                                    >
                                      <SchoolIcon
                                        color="primary"
                                        sx={{ fontSize: 20 }}
                                      />
                                      <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                      >
                                        {form.formacao_cursos_OBJECT
                                          ?.formacoes_descricao || "Curso"}
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {form.formacao_n_OBJECT?.nf_descricao ||
                                        "Nível não informado"}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {form.instituicao} - {form.cidade}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Ano: {form.ano}
                                    </Typography>
                                  </ListItem>
                                  {idx <
                                    candidateInfo.pessoal_formacoes.length -
                                      1 && <Divider />}
                                </React.Fragment>
                              ),
                            )}
                          </List>
                        ) : (
                          <Box p={3} textAlign="center">
                            <SchoolIcon
                              color="primary"
                              sx={{ fontSize: 48, opacity: 0.3, mb: 1 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              Sem informações de formação
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Tab Documentos */}
                    {infoTabValue === 4 && (
                      <List sx={{ p: 0 }}>
                        {candidateInfo.pessoal_cpf && (
                          <>
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: "grey.100" }}>
                                  <BadgeIcon color="primary" />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    CPF
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="body2">
                                    {candidateInfo.pessoal_cpf}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            <Divider />
                          </>
                        )}

                        {candidateInfo.pessoal_rg && (
                          <>
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: "grey.100" }}>
                                  <BadgeIcon color="primary" />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    RG
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="body2">
                                    {candidateInfo.pessoal_rg}
                                    {candidateInfo.pessoal_rgemissor &&
                                      ` - ${candidateInfo.pessoal_rgemissor}`}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            <Divider />
                          </>
                        )}

                        {candidateInfo.pessoal_ctps && (
                          <>
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: "grey.100" }}>
                                  <BadgeIcon color="primary" />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    CTPS
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="body2">
                                    {candidateInfo.pessoal_ctps}
                                    {candidateInfo.pessoal_ctpsserie &&
                                      ` - Série: ${candidateInfo.pessoal_ctpsserie}`}
                                    {candidateInfo.pessoal_ctpsuf &&
                                      ` - ${candidateInfo.pessoal_ctpsuf}`}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            <Divider />
                          </>
                        )}

                        {candidateInfo.pessoal_pis && (
                          <>
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: "grey.100" }}>
                                  <BadgeIcon color="primary" />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    PIS
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="body2">
                                    {candidateInfo.pessoal_pis}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            <Divider />
                          </>
                        )}

                        {candidateInfo.pessoal_cnh && (
                          <ListItem>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: "grey.100" }}>
                                <BadgeIcon color="primary" />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  CNH
                                </Typography>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2">
                                    {candidateInfo.pessoal_nocnh ||
                                      "Possui CNH"}
                                  </Typography>
                                  {candidateInfo.pessoal_tpcnh && (
                                    <Chip
                                      label={`Categoria ${candidateInfo.pessoal_tpcnh}`}
                                      size="small"
                                      sx={{
                                        mt: 0.5,
                                        height: 20,
                                        fontSize: "0.7rem",
                                      }}
                                    />
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        )}
                      </List>
                    )}
                  </Box>
                </>
              ) : (
                <Box p={3} textAlign="center">
                  <PersonIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Informações do candidato não disponíveis
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Popover>
    </StyledDialog>
  );
};

export default ModalChat;
